import { Component } from "@theme/component"
import { VariantSelectedEvent, VariantUpdateEvent } from "@theme/events"
import { morph } from "@theme/morph"
import { syncDiscountBadge } from "@theme/discount-badge"
import { resolveBestMerchantVariant } from "@theme/merchant-resolver"

export default class VariantCustomPicker extends Component {
  isHiddenOption(name) {
    const list = this.hiddenOptions || []
    const n = (name || "").toLowerCase()
    return list.some((h) => n.includes(h))
  }

  #pendingRequestUrl
  #abortController

  connectedCallback() {
    super.connectedCallback()
    this.$scope = this.closest(".product__options-combined") ?? this

    this._onConfigSelectChange = (event) => {
      const target = event.target
      if (!(target instanceof HTMLSelectElement)) return
      if (!target.matches("select[data-config-key]")) return
      this.updateInventoryHint()
    }
    if (this.$scope && this.$scope !== this) {
      this.$scope.addEventListener("change", this._onConfigSelectChange)
    }

    this.fabricAllValid = true
    this._onFabricValidity = (e) => {
      this.fabricAllValid = !!e?.detail?.allValid
      this.updateInventoryHint?.()
    }
    window.addEventListener("fabric:validity", this._onFabricValidity)

    this.configAllValid = true
    this._onConfigValidity = (e) => {
      this.configAllValid = !!e?.detail?.allValid
      this.updateInventoryHint?.()
    }
    window.addEventListener("config:validity", this._onConfigValidity)

    this.dateAllValid = true
    this._onDateValidity = (e) => {
      this.dateAllValid = !!e?.detail?.allValid
      this.updateInventoryHint?.()
    }
    window.addEventListener("refurbishment-date:validity", this._onDateValidity)

    this.hiddenOptions = (this.dataset.hiddenOptions?.split(",") || ["Merchant"]).map((s) => s.trim().toLowerCase()).filter(Boolean)
    this.addEventListener("change", this.variantChanged.bind(this))
    try {
      const pd = this.querySelector('script[type="application/json"]')?.textContent
      this._product = pd ? JSON.parse(pd) : null
    } catch (e) {
      console.warn("Product data unreadable", e)
    }

    if (this._product) {
      const earlySync = () => {
        const selectedVariant = this.findSelectedVariant()
        if (selectedVariant) {
          const variantInput = this.$scope?.querySelector("input[data-selected-variant-id]")
          if (variantInput) {
            variantInput.setAttribute("value", String(selectedVariant.id))
          }
        }
      }

      // Get selected variant early
      earlySync()

      // try again with short delay if DOM not ready yet
      setTimeout(earlySync, 10)
    }

    this.initializeWhenReady()

    document.addEventListener("shopify:section:load", () => {
      // Shopify section loaded, re-initialise
      this.performInitialization()
    })

    window.addEventListener("cart:add", this.handleCartAdd)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener("cart:add", this.handleCartAdd)
    window.removeEventListener("fabric:validity", this._onFabricValidity)
    window.removeEventListener("config:validity", this._onConfigValidity)
    window.removeEventListener("refurbishment-date:validity", this._onDateValidity)
    if (this.$scope && this.$scope !== this) {
      this.$scope.removeEventListener("change", this._onConfigSelectChange)
    }
  }

  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return

    this.updateSelectedOption(event.target)
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? "" }))

    const isOnProductPage = this.isProductPage()

    const currentUrl = this.dataset.productUrl?.split("?")[0]
    const newUrl = event.target.dataset.connectedProductUrl
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl

    this.fetchUpdatedSection(this.buildRequestUrl(event.target), loadsNewProduct)

    const url = new URL(window.location.href)
    let variantId = null

    if (event.target instanceof HTMLInputElement && event.target.type === "radio") {
      variantId = event.target.dataset.variantId || null
    } else if (event.target instanceof HTMLSelectElement) {
      const selectedOption = event.target.options[event.target.selectedIndex]
      variantId = selectedOption?.dataset.variantId || null
    }

    if (isOnProductPage) {
      if (variantId) url.searchParams.set("variant", variantId)
      else url.searchParams.delete("variant")
    }
    if (loadsNewProduct && newUrl) url.pathname = newUrl

    this.updateInventoryHint()
    this.highlightOptionsWithInventory()

    if (url.href !== window.location.href) {
      history.replaceState({}, "", url.toString())
    }

    setTimeout(() => {
      this.syncVariantFromSelection()
    }, 50)
  }

  updateSelectedOption(target) {
    if (typeof target === "string") {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`)
      if (!targetElement) throw new Error("Target element not found")
      target = targetElement
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue)
      if (!newSelectedOption) throw new Error("Option not found")

      for (const option of target.options) {
        option.removeAttribute("selected")
      }
      newSelectedOption.setAttribute("selected", "selected")
    }
  }

  async updateInventoryHint() {
    const product =
      this._product ||
      (() => {
        const el = this.querySelector('script[type="application/json"]')
        return el ? JSON.parse(el.textContent) : null
      })()
    if (!product) return

    const minimumOrderQuantity = this.getMinimumOrderQuantity(product)
    const variants = product.variants ?? []
    const allOptions = product.options ?? []
    const visibleOptions = allOptions.filter((opt) => !this.isHiddenOption(opt))

    const selected = {}
    visibleOptions.forEach((optionName) => {
      const sel = this.querySelector(`select[name="options[${optionName}]"]`)
      if (sel instanceof HTMLSelectElement) {
        selected[optionName] = sel.value
        return
      }
      const fs = this.querySelector(`.variant-option [data-option-name="${optionName}"]`)?.closest(".variant-option")
      const checked = fs?.querySelector('input[type="radio"]:checked')
      if (checked) selected[optionName] = checked.value
    })

    const oos = !variants.some(
      (v) =>
        v.available &&
        visibleOptions.every((opt) => {
          const selVal = selected[opt]
          if (!selVal) return true
          const idx = allOptions.indexOf(opt)
          return v[`option${idx + 1}`] === selVal
        }),
    )

    let matchingVariants = []
    const fallbackToSingleVariant = !visibleOptions.length || variants.length === 1

    if (fallbackToSingleVariant) {
      matchingVariants = variants
    } else {
      matchingVariants = variants.filter((variant) =>
        visibleOptions.every((opt) => {
          const optIndex = allOptions.indexOf(opt)
          return variant[`option${optIndex + 1}`] === selected[opt]
        }),
      )
    }

    const selectedVariant = this.findSelectedVariant()
    const bestVariant = selectedVariant || matchingVariants.find((v) => v.available) || matchingVariants[0]

    const availableMatchingVariants = matchingVariants.filter((variant) => variant?.available)
    const hasUntrackedInventory = availableMatchingVariants.some((variant) => !this.hasInventoryManagement(variant))
    const trackedTotalQty = matchingVariants.reduce((sum, variant) => {
      if (!this.hasInventoryManagement(variant)) return sum
      return sum + Number(variant.inventory_quantity ?? 0)
    }, 0)

    let inCartQty = 0
    if (bestVariant) {
      try {
        const res = await fetch("/cart.js")
        const cart = await res.json()
        inCartQty = cart.items.filter((item) => item.id === bestVariant.id).reduce((sum, item) => sum + item.quantity, 0)
      } catch (err) {
        console.warn("Could not load cart:", err)
      }
    }

    // Bundle classification:
    //   Configurator (single component, multiple option dims): bundle variants 1:1 mirror the
    //     component variant, trackedTotalQty nets per-selection across hidden dims and is the
    //     source of truth. The per-option-value bundle aggregate mixes dimensions and can break
    //     with oversold negatives, so we skip it.
    //   Mix & Match (multiple components, one option dim each): bundle variants may be
    //     synthesized as per-factory min(componentA, componentB). Summing across factories is
    //     misleading — oversold negatives drag totals down, and the same base can show
    //     different counts depending on which shell is picked. The per-component caps
    //     (min of selected option totals) are the authoritative cross-factory buildable count.
    const isBundle = product.is_bundle === true
    const hideQuantities = product.hide_quantities === true
    const bundleVariantsAreTracked = matchingVariants.some((variant) => this.hasInventoryManagement(variant))
    const isSingleComponentBundle = Number(product.bundle_components_count) === 1
    const isMixAndMatch = isBundle && !isSingleComponentBundle
    const hasBundleInventoryKeys = Array.isArray(product.bundle_inventory_keys) && product.bundle_inventory_keys.length > 0

    let effectiveMax
    if (hasUntrackedInventory) {
      effectiveMax = Number.POSITIVE_INFINITY
    } else if (isMixAndMatch && bundleVariantsAreTracked) {
      // Don't seed from trackedTotalQty — it's broken for Mix & Match. The component cap
      // below sets the authoritative bound.
      effectiveMax = Number.POSITIVE_INFINITY
    } else {
      effectiveMax = Math.max(trackedTotalQty - inCartQty, 0)
    }

    const skipBundleCap = isSingleComponentBundle && bundleVariantsAreTracked
    const hasBundleComponentData = isBundle && !hideQuantities && !skipBundleCap && hasBundleInventoryKeys

    if (hasBundleComponentData) {
      let minBundleInventory = Number.POSITIVE_INFINITY
      for (const optionName of visibleOptions) {
        const selectedValue = selected[optionName]
        if (selectedValue) {
          const bundleInv = this.getBundleInventoryForValue(selectedValue)
          if (bundleInv !== null && bundleInv < minBundleInventory) {
            minBundleInventory = bundleInv
          }
        }
      }
      if (minBundleInventory !== Number.POSITIVE_INFINITY) {
        const cap = Math.max(minBundleInventory - inCartQty, 0)
        effectiveMax = effectiveMax === Number.POSITIVE_INFINITY ? cap : Math.min(effectiveMax, cap)
      }
    }

    // hide_quantities makes the Liquid overwrite every bundle variant's inventory_quantity with
    // bundle_min_sum (see custom-variant-picker.liquid: variant_qty = min_sum). trackedTotalQty
    // then sums that same replicated total across the hidden dimension (e.g. Merchant/factory),
    // so effectiveMax becomes 37 + 37 = 74 even though only bundle_min_sum units are buildable.
    // The component-cap block above is skipped for tracked single-component bundles (skipBundleCap),
    // so cap here instead. Scoped to hide_quantities for the same reason as
    // getBundlePerComboInventory: when quantities are shown, variants carry real per-combo values
    // and the netted trackedTotalQty is authoritative.
    if (isBundle && hideQuantities && !hasUntrackedInventory) {
      const minSum = Number(product.bundle_min_sum)
      if (Number.isFinite(minSum) && minSum >= 0) {
        const cap = Math.max(minSum - inCartQty, 0)
        effectiveMax = effectiveMax === Number.POSITIVE_INFINITY ? cap : Math.min(effectiveMax, cap)
      }
    }

    // Product limit cap: custom.total_product_quantity / available_product_quantity bound what the
    // product can fulfil, e.g. a configurator whose shared base is scarcer than the variant stock
    // summed across hidden options (Merchant).
    const productLimit = product.product_quantity_limit
    if (productLimit != null && Number.isFinite(Number(productLimit))) {
      const cap = Math.max(Number(productLimit) - inCartQty, 0)
      effectiveMax = effectiveMax === Number.POSITIVE_INFINITY ? cap : Math.min(effectiveMax, cap)
    }

    // Product-mix cap: a selected "product mix" option (see product-mix-products.liquid) can be
    // scarcer than the main product itself. Cap effectiveMax to the minimum tracked availability
    // across all currently selected product-mix picks within this picker's scope.
    const mixSelects = Array.from(this.$scope?.querySelectorAll("select[data-mix-cap]") ?? [])
    let minMixInventory = Number.POSITIVE_INFINITY
    for (const select of mixSelects) {
      const selectedOption = select.options[select.selectedIndex]
      if (!selectedOption?.value) continue
      if (selectedOption.dataset.mixInventoryTracked !== "true") continue
      const qty = Number(selectedOption.dataset.mixAvailableQuantity ?? 0)
      if (qty < minMixInventory) minMixInventory = qty
    }
    if (minMixInventory !== Number.POSITIVE_INFINITY) {
      const cap = Math.max(minMixInventory - inCartQty, 0)
      effectiveMax = effectiveMax === Number.POSITIVE_INFINITY ? cap : Math.min(effectiveMax, cap)
    }

    // effectiveMax starts as Infinity when the product's own variant is untracked, but a scarcer
    // selected product-mix / bundle component can still pull it down to a real, finite number
    // (see the caps applied above). Number.isFinite(effectiveMax) — not hasUntrackedInventory —
    // is therefore the right signal for "do we actually have a number to show/enforce": it's true
    // whenever some tracked signal (own variant, bundle component, or mix selection) capped it,
    // and false only when nothing at all constrains this product.
    const hasKnownMax = Number.isFinite(effectiveMax)
    const hasAnyInventory = !hasKnownMax || effectiveMax > 0 || trackedTotalQty > 0
    const canMeetMinimum = !hasKnownMax || effectiveMax >= minimumOrderQuantity

    const allConfigSelectsValid = Array.from(this.$scope.querySelectorAll("select[data-config-key]"))
      .filter((select) => !select.disabled)
      .every((select) => (select.value ?? "").trim().length > 0)
    // Additional products (product-mix / configuration_products, see product-mix-products.liquid
    // and product-configuration-products.liquid) render one required select[data-upgrade-product]
    // per attached product. `this.configAllValid` used to be the source of truth for this, but it
    // is only updated via the async "config:validity" broadcast from <product-configuration-product>
    // (product-configuration-product.js), which queries `document.querySelectorAll(...)` — a check
    // that isn't scoped to this picker at all. That made it unreliable in both directions: it can
    // stay stuck at its `true` default if the broadcast's listener registers too late, and it can
    // just as easily stay stuck at `false` if the broadcast races with this component's own state
    // updates. Checking the selects directly here instead removes the dependency on that async,
    // unscoped signal entirely — it's always in sync with what's actually in the DOM, for this
    // product only. Products without such selects (e.g. plain multi-variant refurbishment products)
    // simply have none, so this is always `true` for them.
    const allUpgradeSelectsValid = Array.from(this.$scope.querySelectorAll("select[data-upgrade-product]"))
      .filter((select) => !select.disabled)
      .every((select) => (select.value ?? "").trim().length > 0)
    // Specifically the product-mix components (select[data-mix-cap], a subset of the
    // data-upgrade-product selects above — configuration products don't carry data-mix-cap): every
    // category (Armlehne, Mechanik, ...) needs a pick before the mix cap computed above reflects
    // the actual combination rather than just whichever single category happens to be chosen so
    // far. Reuses `mixSelects`, already queried for the cap calculation above.
    const allMixSelectsValid = mixSelects.filter((select) => !select.disabled).every((select) => (select.value ?? "").trim().length > 0)

    const hintContainer = this.$scope.querySelector("[data-inventory-hint]")
    const availableQuantityHint = hintContainer?.querySelector("span") ?? null
    const inCartHint = this.$scope.querySelector("[data-incart-hint]")
    const inCartHintValue = this.$scope.querySelector("[data-incart-hint] > span")
    if (hintContainer) {
      // For a product whose own variant isn't tracked, a number only becomes meaningful once
      // every product-mix component has been picked — before that, effectiveMax may just reflect
      // whichever single category happens to be chosen so far, not the actual combination the
      // customer will end up ordering. Keep the hint hidden until all mix components are chosen
      // instead of showing a number (or "-") that's still subject to change. Products with a
      // tracked own variant show it immediately, as before; products with no product-mix
      // categories at all (mixSelects empty) are trivially "fully configured" too.
      // The same applies to a tracked product: with mix categories still open, the number only
      // reflects the main product / bundle components (e.g. 810 backs) and not yet the scarcer
      // mix pick (e.g. 402 base crosses), so hide it until every mix component is chosen.
      hintContainer.hidden = !allMixSelectsValid
    }
    if (availableQuantityHint) {
      const hintValue = hasKnownMax ? `${effectiveMax}` : "-"
      availableQuantityHint.textContent = hintValue
    }
    if (inCartHint && inCartHintValue) {
      if (inCartQty > 0) {
        inCartHintValue.textContent = `${inCartQty}`
        inCartHint.classList.add("is-visible")
      } else {
        inCartHintValue.textContent = "0"
        inCartHint.classList.remove("is-visible")
      }
    }

    const quantityInput = this.$scope.querySelector("[data-quantity-input]")
    if (quantityInput) {
      const current = Number(quantityInput.value) || 0
      if (hasKnownMax) {
        quantityInput.max = String(effectiveMax)
      } else {
        quantityInput.removeAttribute("max")
      }
      quantityInput.min = String(minimumOrderQuantity)

      const shouldDisableQuantity = !hasAnyInventory || !canMeetMinimum
      quantityInput.disabled = shouldDisableQuantity

      if (shouldDisableQuantity) {
        quantityInput.value = "0"
      } else {
        let nextValue = current
        if (nextValue < minimumOrderQuantity) nextValue = minimumOrderQuantity
        const exceedsMax = hasKnownMax && nextValue > effectiveMax
        if (current === 0 || exceedsMax) {
          nextValue = minimumOrderQuantity
        }
        quantityInput.value = String(nextValue)
      }
    }

    const addToCartButton = this.$scope.querySelector("[data-add-to-cart]")
    if (addToCartButton) {
      // allConfigSelectsValid / allUpgradeSelectsValid are computed once above (alongside
      // allMixSelectsValid for the inventory-hint gate) and reused here for ATC gating.
      // Same reasoning for fabric pickers (product-fabric-color-selector.liquid): mirror
      // variant-fabric-picker.js's own pickerHasValue() check, but scoped to this picker instead of
      // `this.fabricAllValid`, which relies on the same kind of unscoped, async "fabric:validity"
      // broadcast (and is additionally gated behind that component's own `await fetch()` on connect).
      const allFabricPickersValid = Array.from(this.$scope.querySelectorAll("variant-fabric-picker")).every((picker) => {
        if (picker.hasAttribute("data-optional")) return true
        const input = picker.querySelector("[data-accessory-variant-id]")
        if (!input) return false
        const value = (input.value ?? "").trim()
        const zeroish = /^(?:0|€?\s*0(?:[.,]00)?)$/
        return !!value && value !== "null" && value !== "undefined" && !zeroish.test(value)
      })
      const dateInput = this.$scope.querySelector('.refurbishment-date input[type="date"][required]')
      const dateValid = !dateInput || (!!dateInput.value && !dateInput.classList.contains('refurbishment-date--invalid'))
      const canAtc = hasAnyInventory && (!hasKnownMax || effectiveMax > 0) && canMeetMinimum && allFabricPickersValid && dateValid && allConfigSelectsValid && allUpgradeSelectsValid
      if (!canAtc) {
        addToCartButton.setAttribute("disabled", "disabled")
        addToCartButton.classList.add("is-disabled")
      } else {
        addToCartButton.removeAttribute("disabled")
        addToCartButton.classList.remove("is-disabled")
      }
    }
  }

  handleCartAdd = () => {
    this.updateInventoryHint()
  }

  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl
    this.#pendingRequestUrl = productUrl
    const params = []

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(",")}`)
    } else if (source === "product-card") {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(",")}`)
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`)
      }
    }

    if (this.closest("quick-add-component") || this.closest("swatches-variant-picker-component")) {
      if (productUrl?.includes("?")) {
        productUrl = productUrl.split("?")[0]
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join("&")}`
    }
    return `${productUrl}?${params.join("&")}`
  }

  getMatchingInventorySum({ variants, allOptions, hiddenOptions, visibleOptions, selected, targetOption, targetValue }) {
    const normalize = (name) => String(name ?? "").toLowerCase()
    const normalizeValue = (value) =>
      String(value ?? "")
        .trim()
        .toLowerCase()
    const hiddenPatterns = Array.from(hiddenOptions ?? [], (name) => normalize(name))
    const isHidden = (name) => hiddenPatterns.some((pattern) => normalize(name).includes(pattern))
    const orderedVisibleOptions = Array.isArray(visibleOptions) && visibleOptions.length ? visibleOptions : allOptions.filter((opt) => !isHidden(opt))

    return variants.reduce((sum, variant) => {
      if (!variant?.available) return sum

      const optIndex = allOptions.indexOf(targetOption)
      if (optIndex === -1) return sum
      if (normalizeValue(variant[`option${optIndex + 1}`]) !== normalizeValue(targetValue)) return sum

      for (const opt of orderedVisibleOptions) {
        if (opt === targetOption) continue
        const selVal = selected[opt]
        if (!selVal) continue
        const selIdx = allOptions.indexOf(opt)
        if (selIdx === -1) continue
        if (normalizeValue(variant[`option${selIdx + 1}`]) !== normalizeValue(selVal)) return sum
      }

      if (!this.hasInventoryManagement(variant)) return Number.POSITIVE_INFINITY
      if (!Number.isFinite(sum)) return sum
      return sum + (variant.inventory_quantity ?? 0)
    }, 0)
  }

  getVariantsForCounting() {
    // For bundles, we use the regular variants (not bundle_component_variants)
    // Bundle inventory is handled separately via getBundleInventoryForValue()
    return this._product?.variants ?? []
  }

  /**
   * For bundles: Get the total inventory for a specific option VALUE (not position).
   * This sums up all inventory entries where the key matches the option value.
   * If bundle_inventory_keys are empty, falls back to regular variant data.
   * @param {string} optionValue - The option value to look up (e.g., "S", "Red")
   * @returns {number|null} - Total inventory for this option value, or null if not a bundle
   */
  getBundleInventoryForValue(optionValue) {
    const product = this._product
    if (!product?.is_bundle) return null

    const keys = product.bundle_inventory_keys ?? []
    const qtys = product.bundle_inventory_qtys ?? []
    // Max buildable bundles is capped by the scarcest component's total inventory.
    // A single option value can never make more bundles than that overall cap.
    const minSum = Number(product.bundle_min_sum)
    const hasMinSumCap = Number.isFinite(minSum) && minSum >= 0

    const normalizeValue = (v) => String(v ?? "").trim().toLowerCase()
    const targetNormalized = normalizeValue(optionValue)

    // If we have bundle component inventory data, use it
    if (keys.length > 0 && keys.length === qtys.length) {
      let total = 0
      for (let i = 0; i < keys.length; i++) {
        const keyNormalized = normalizeValue(keys[i])
        if (keyNormalized === targetNormalized) {
          total += Number(qtys[i]) || 0
        }
      }
      if (hasMinSumCap && total > minSum) {
        total = minSum
      }
      return total
    }

    // Fallback: Use regular variant data for bundles without component inventory
    // Sum inventory for all variants that have this option value
    const variants = product.variants ?? []
    const allOptions = product.options ?? []

    let total = 0
    for (const variant of variants) {
      if (!variant?.available) continue
      if (!this.hasInventoryManagement(variant)) continue

      // Check if any of the variant's options match the target value
      for (let optIdx = 0; optIdx < allOptions.length; optIdx++) {
        const variantOptValue = variant[`option${optIdx + 1}`]
        if (normalizeValue(variantOptValue) === targetNormalized) {
          total += Number(variant.inventory_quantity) || 0
          break // Count this variant only once
        }
      }
    }

    if (hasMinSumCap && total > minSum) {
      total = minSum
    }

    return total
  }

  /**
   * Per-combo-aware inventory total for a single option value on a tracked bundle.
   *
   * Mirrors the Liquid logic in custom-variant-picker.liquid: groups bundle variants by the
   * values of OTHER visible options (excluding already-selected ones and the target option),
   * nets inventory_quantity within each group (this collapses hidden dimensions like factory),
   * clamps each group sum at 0 so oversold sub-combos don't subtract from unrelated buildable
   * combos, then sums the clamped groups.
   *
   * Use only when bundle variants carry their own inventory_management (Configurator-mirror).
   * For untracked bundles, fall back to component-aggregate paths.
   */
  getBundlePerComboInventory({ targetOption, targetValue, selected = {}, visibleOptions, allOptions }) {
    const product = this._product
    if (!product) return 0
    const variants = product.variants ?? []
    const normalize = (v) =>
      String(v ?? "")
        .trim()
        .toLowerCase()
    const targetOptIdx = allOptions.indexOf(targetOption)
    if (targetOptIdx === -1) return 0
    const targetNormalized = normalize(targetValue)

    const matchingVariants = variants.filter((v) => {
      if (!v || !this.hasInventoryManagement(v)) return false
      if (normalize(v[`option${targetOptIdx + 1}`]) !== targetNormalized) return false
      for (const optName of Object.keys(selected)) {
        if (!selected[optName]) continue
        const idx = allOptions.indexOf(optName)
        if (idx === -1) continue
        if (normalize(v[`option${idx + 1}`]) !== normalize(selected[optName])) return false
      }
      return true
    })

    const otherVisibleOpts = visibleOptions.filter(
      (o) => o !== targetOption && !Object.prototype.hasOwnProperty.call(selected, o),
    )

    const groupSums = new Map()
    for (const v of matchingVariants) {
      const sig = otherVisibleOpts
        .map((opt) => {
          const idx = allOptions.indexOf(opt)
          return idx === -1 ? "" : String(v[`option${idx + 1}`] ?? "")
        })
        .join("␟")
      groupSums.set(sig, (groupSums.get(sig) ?? 0) + Number(v.inventory_quantity ?? 0))
    }

    let total = 0
    for (const sum of groupSums.values()) {
      if (sum > 0) total += sum
    }

    // When hide_quantities is on, the Liquid overrides every bundle variant's inventory_quantity
    // to bundle_min_sum (see custom-variant-picker.liquid: variant_qty = min_sum). The real
    // per-combo split (e.g. Factory 01=2, Factory 02=35) is discarded — every variant reports the
    // same replicated total. Grouping-and-summing that across an untracked dimension (e.g. Version,
    // which isn't in bundle_inventory_keys) then multiplies the shared stock (37 + 37 = 74) even
    // though only bundle_min_sum units are buildable. Cap at bundle_min_sum to undo the double count.
    //
    // We do this ONLY for hide_quantities: when quantities are shown, variants carry their real
    // per-combo values and the group-net-and-sum result is authoritative — capping there would
    // regress the oversold-mix Configurator case (negative variants dragging min_sum below the
    // true buildable total), which is exactly why the Liquid skips the cap for single-component
    // bundles. The minSum >= 0 guard is a further safety net against a negative replicated total.
    const minSum = Number(product.bundle_min_sum)
    if (product.hide_quantities === true && Number.isFinite(minSum) && minSum >= 0 && total > minSum) {
      total = minSum
    }

    // Same product-level cap as the Liquid labels and the inventory hint: stock repeated per hidden
    // option value (e.g. Merchant) must not exceed what the product can fulfil. A limit of 0 is ignored.
    const productLimit = Number(product.product_quantity_limit)
    if (product.product_quantity_limit != null && Number.isFinite(productLimit) && productLimit > 0 && total > productLimit) {
      total = productLimit
    }

    return total
  }

  getOptionInventoryTotal({ variants = this.getVariantsForCounting(), allOptions, targetOption, targetValue }) {
    const normalizeValue = (value) =>
      String(value ?? "")
        .trim()
        .toLowerCase()
    const optIndex = allOptions.indexOf(targetOption)
    if (optIndex === -1) return 0
    const targetValueNormalized = normalizeValue(targetValue)

    return variants.reduce((sum, variant) => {
      if (!variant) return sum
      const variantValue = variant[`option${optIndex + 1}`]
      if (normalizeValue(variantValue) !== targetValueNormalized) return sum

      if (!this.hasInventoryManagement(variant)) return Number.POSITIVE_INFINITY

      const quantity = Math.max(0, Number(variant.inventory_quantity ?? 0))
      if (quantity <= 0) return sum
      if (!Number.isFinite(sum)) return sum
      return sum + quantity
    }, 0)
  }

  shouldHideCountsForElement(element) {
    if (!element) return false
    if (element instanceof HTMLOptionElement) {
      const parent = element.parentElement
      return !!parent?.hasAttribute("data-hide-quantities")
    }
    if (element instanceof HTMLElement) {
      if (element.hasAttribute("data-hide-quantities")) return true
      return !!element.closest("[data-hide-quantities]")
    }
    return false
  }

  highlightOptionsWithInventory() {
    const product = this._product
    if (!product) return

    const variants = product.variants ?? []
    const allOptions = product.options ?? []
    const normalizeName = (name) =>
      String(name ?? "")
        .trim()
        .toLowerCase()
    const normalizeValue = (value) =>
      String(value ?? "")
        .trim()
        .toLowerCase()
    const hiddenPatterns = Array.from(this.hiddenOptions ?? [], (opt) => normalizeName(opt))
    const isHidden = (name) => hiddenPatterns.some((pattern) => normalizeName(name).includes(pattern))
    const visibleOptions = allOptions.filter((opt) => !isHidden(opt))

    const selected = {}
    visibleOptions.forEach((opt) => {
      const field = this.querySelector(`select[name="options[${opt}]"], fieldset[name^="${opt}"]`)
      if (field?.tagName === "SELECT") {
        selected[opt] = field.value
      } else {
        const checked = this.querySelector(`input[name^="${opt}"]:checked`)
        if (checked) selected[opt] = checked.value
      }
    })

    const optionButtons = this.querySelectorAll("[data-option-name][data-option-value]")
    for (const el of optionButtons) {
      const optName = el.dataset.optionName
      const optValue = el.dataset.optionValue
      const canonicalOptionName = allOptions.find((name) => normalizeName(name) === normalizeName(optName)) ?? optName

      const comboSum = this.getMatchingInventorySum({
        variants,
        allOptions,
        hiddenOptions: hiddenPatterns,
        visibleOptions,
        selected,
        targetOption: canonicalOptionName,
        targetValue: optValue,
      })

      const hasInventory = Number.isFinite(comboSum) ? comboSum > 0 : true
      el.classList.toggle("has-inventory", hasInventory)
    }

    const optionSelectOptions = this.querySelectorAll("select option[data-option-name][data-option-value]")
    for (const el of optionSelectOptions) {
      const optName = el.dataset.optionName
      const optValue = el.dataset.optionValue
      const canonicalOptionName = allOptions.find((name) => normalizeName(name) === normalizeName(optName)) ?? optName

      const comboSum = this.getMatchingInventorySum({
        variants,
        allOptions,
        hiddenOptions: hiddenPatterns,
        visibleOptions,
        selected,
        targetOption: canonicalOptionName,
        targetValue: optValue,
      })

      const hasInventory = Number.isFinite(comboSum) ? comboSum > 0 : true
    }

    // Update dependent option labels with inventory counts
    this.updateDependentOptionLabels()
  }

  /**
   * Updates the inventory labels for dependent options (option 2+).
   * Option 1 shows total inventory across all combinations.
   * Option 2+ shows inventory filtered by the selection in previous options.
   */
  updateDependentOptionLabels() {
    const product = this._product
    if (!product) return

    // Skip if quantities should be hidden
    // For bundles: respect hide_quantities metafield
    // For non-bundles: only show if show_quantities tag is set
    const isBundle = product.is_bundle === true
    if (isBundle && product.hide_quantities) return
    if (!isBundle && !product.show_quantities_tag) return

    const variants = this.getVariantsForCounting()
    const allOptions = product.options ?? []
    const normalizeName = (name) => String(name ?? "").trim().toLowerCase()
    const normalizeValue = (value) => String(value ?? "").trim().toLowerCase()
    const hiddenPatterns = Array.from(this.hiddenOptions ?? [], (opt) => normalizeName(opt))
    const isHidden = (name) => hiddenPatterns.some((pattern) => normalizeName(name).includes(pattern))
    const visibleOptions = allOptions.filter((opt) => !isHidden(opt))

    // Nothing to do if we have 0 or 1 visible options
    if (visibleOptions.length < 2) return

    // Get current selections
    const selected = {}
    visibleOptions.forEach((opt) => {
      const sel = this.querySelector(`select[name="options[${opt}]"]`)
      if (sel instanceof HTMLSelectElement) {
        selected[opt] = sel.value
        return
      }
      const fs = this.querySelector(`.variant-option [data-option-name="${opt}"]`)?.closest(".variant-option")
      const checked = fs?.querySelector('input[type="radio"]:checked')
      if (checked) selected[opt] = checked.value
    })

    // Get translations for labels
    const labelElement = this.querySelector("[data-inventory-label]")
    const availableLabel = labelElement?.dataset?.inventoryLabel || "available"
    const unavailableLabel = labelElement?.dataset?.unavailableLabel || "currently unavailable"
    const withSelectedLabel = labelElement?.dataset?.withSelectedLabel || "with selected"
    const ofLabel = labelElement?.dataset?.ofLabel || "of"

    // Process each visible option
    visibleOptions.forEach((optionName, optionIndex) => {
      const isFirstOption = optionIndex === 0

      // Find all elements for this option (both buttons and select options)
      const optionElements = this.querySelectorAll(`[data-option-name="${optionName}"][data-option-value]`)

      for (const el of optionElements) {
        const optValue = el.dataset.optionValue
        if (!optValue) continue

        // Skip if this element has hide-quantities
        if (this.shouldHideCountsForElement(el)) continue

        let inventoryCount

        // Check if this is a bundle product
        const isBundle = product.is_bundle === true

        // Check if bundle has component inventory data
        const hasBundleComponentData =
          isBundle && Array.isArray(product.bundle_inventory_keys) && product.bundle_inventory_keys.length > 0

        // Configurator-style bundle (single component, multiple option dimensions, bundle
        // variants 1:1 mirror): per-combo netting off bundle variants is authoritative. Mix &
        // Match bundles (multiple components) keep the legacy component-cap path below.
        const bundleVariantsAreTracked = isBundle && variants.some((v) => this.hasInventoryManagement(v))
        const isSingleComponentBundle = Number(product.bundle_components_count) === 1
        const useBundlePerCombo = isBundle && bundleVariantsAreTracked && isSingleComponentBundle

        if (useBundlePerCombo) {
          const filterSelected = {}
          for (let i = 0; i < optionIndex; i++) {
            const prevOpt = visibleOptions[i]
            if (selected[prevOpt]) {
              filterSelected[prevOpt] = selected[prevOpt]
            }
          }
          inventoryCount = this.getBundlePerComboInventory({
            targetOption: optionName,
            targetValue: optValue,
            selected: filterSelected,
            visibleOptions,
            allOptions,
          })
        } else if (hasBundleComponentData && isFirstOption) {
          // Bundle products with component data (no own tracking), Option 1: use bundle inventory lookup
          const bundleInventory = this.getBundleInventoryForValue(optValue)
          inventoryCount = bundleInventory ?? 0
        } else if (hasBundleComponentData && !isFirstOption) {
          // Bundle with component data (Mix & Match, or untracked bundle), Option 2+:
          // Use the per-component caps. The bundle variants here are unreliable for
          // cross-factory totals — Mix & Match bundle variants may carry synthesized
          // per-factory min(componentA, componentB) values, and summing them across
          // factories produces misleading numbers (oversold negatives drag the total
          // and the same base shows different counts per shell colour).
          const currentOptBundleInventory = this.getBundleInventoryForValue(optValue)

          let minPreviousInventory = Number.POSITIVE_INFINITY
          for (let i = 0; i < optionIndex; i++) {
            const prevOpt = visibleOptions[i]
            const prevValue = selected[prevOpt]
            if (prevValue) {
              const prevInventory = this.getBundleInventoryForValue(prevValue)
              if (prevInventory !== null && prevInventory < minPreviousInventory) {
                minPreviousInventory = prevInventory
              }
            }
          }

          let bundleCap = Number.POSITIVE_INFINITY
          if (currentOptBundleInventory !== null) {
            bundleCap = currentOptBundleInventory
          }
          if (minPreviousInventory !== Number.POSITIVE_INFINITY && minPreviousInventory < bundleCap) {
            bundleCap = minPreviousInventory
          }
          inventoryCount = bundleCap === Number.POSITIVE_INFINITY ? 0 : Math.max(0, bundleCap)
        } else if (isFirstOption) {
          // First option: show total inventory for this option value (across all combinations)
          inventoryCount = this.getOptionInventoryTotal({
            variants,
            allOptions,
            targetOption: optionName,
            targetValue: optValue,
          })
        } else {
          // Dependent options: show inventory filtered by previous option selections
          const filterSelected = {}
          // Only include selections from options BEFORE this one
          for (let i = 0; i < optionIndex; i++) {
            const prevOpt = visibleOptions[i]
            if (selected[prevOpt]) {
              filterSelected[prevOpt] = selected[prevOpt]
            }
          }

          inventoryCount = this.getMatchingInventorySum({
            variants,
            allOptions,
            hiddenOptions: hiddenPatterns,
            visibleOptions: visibleOptions.slice(0, optionIndex + 1),
            selected: filterSelected,
            targetOption: optionName,
            targetValue: optValue,
          })
        }

        // For dependent options (Option 2+), get total inventory and first option name
        let totalInventory = null
        let firstOptionName = null
        if (!isFirstOption) {
          // Get total inventory for this option value (without filtering by previous options).
          // Tracked single-component bundles use per-combo netting so deficits stay within their
          // own sub-combo. Mix & Match bundles fall through to the legacy component-aggregate
          // path, where the bundle component sum for the option value is authoritative.
          if (useBundlePerCombo) {
            totalInventory = this.getBundlePerComboInventory({
              targetOption: optionName,
              targetValue: optValue,
              selected: {},
              visibleOptions,
              allOptions,
            })
          } else {
            const bundleInvForTotal = this.getBundleInventoryForValue(optValue)
            const variantInvTotal = this.getOptionInventoryTotal({
              variants,
              allOptions,
              targetOption: optionName,
              targetValue: optValue,
            })

            if (bundleInvForTotal !== null && bundleInvForTotal > 0) {
              totalInventory = bundleInvForTotal
            } else {
              totalInventory = variantInvTotal
            }
          }
          // Get the name of the first visible option
          firstOptionName = visibleOptions[0] || null
        }

        // Update the label text
        this.updateElementInventoryLabel(el, optValue, inventoryCount, availableLabel, unavailableLabel, {
          totalInventory,
          firstOptionName,
          withSelectedLabel,
          ofLabel,
        })
      }
    })
  }

  /**
   * Updates the inventory label for a single element (button or select option)
   * @param {Element} element - The DOM element to update
   * @param {string} baseValue - The option value text
   * @param {number} inventoryCount - The filtered inventory count
   * @param {string} availableLabel - Translation for "available"
   * @param {string} unavailableLabel - Translation for "currently unavailable"
   * @param {Object} [extras] - Additional data for dependent options
   * @param {number|null} [extras.totalInventory] - Total inventory (unfiltered)
   * @param {string|null} [extras.firstOptionName] - Name of the first option (e.g., "Schale")
   * @param {string} [extras.withSelectedLabel] - Translation for "with selected"
   * @param {string} [extras.ofLabel] - Translation for "of"
   */
  updateElementInventoryLabel(element, baseValue, inventoryCount, availableLabel, unavailableLabel, extras) {
    const { totalInventory = null, firstOptionName = null, withSelectedLabel = "", ofLabel = "of" } = extras || {}

    const formatLabel = (value, count) => {
      if (!Number.isFinite(count)) {
        // Untracked inventory
        return value
      }
      const displayCount = Math.max(0, count)
      const displayTotal = totalInventory !== null ? Math.max(0, totalInventory) : null

      // For dependent options: show "X of Y available" or "X of Y with selected [Option1Name]"
      // Set to true to show the "with selected [Option1Name]" suffix instead of "available"
      const showDependentOptionSuffix = false

      if (displayTotal !== null && firstOptionName && Number.isFinite(displayTotal)) {
        if (displayCount <= 0) {
          return `${value} (${unavailableLabel})`
        }
        if (showDependentOptionSuffix) {
          return `${value} (${displayCount} ${ofLabel} ${displayTotal} ${withSelectedLabel} ${firstOptionName})`
        }
        // If count equals totalInventory, just show "X available" instead of "X of X available"
        if (displayCount === displayTotal) {
          return `${value} (${displayCount} ${availableLabel})`
        }
        return `${value} (${displayCount} ${ofLabel} ${displayTotal} ${availableLabel})`
      }
      if (displayCount <= 0) {
        return `${value} (${unavailableLabel})`
      }
      return `${value} (${displayCount} ${availableLabel})`
    }

    const newLabel = formatLabel(baseValue, inventoryCount)

    if (element instanceof HTMLOptionElement) {
      // For select options, update the text content
      const textSpan = element.querySelector(".variant-option__button-label__text")
      if (textSpan) {
        textSpan.textContent = newLabel
      } else {
        element.textContent = newLabel
      }
    } else if (element.tagName === "LABEL") {
      // For button labels, find the text span
      const textSpan = element.querySelector(".variant-option__button-label__text")
      if (textSpan) {
        textSpan.textContent = newLabel
      }
    }
  }

  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    this.#abortController?.abort()
    const controller = new AbortController()
    this.#abortController = controller

    this.#togglePriceUpdatingState(true)

    fetch(requestUrl, { signal: controller.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined
        const html = new DOMParser().parseFromString(responseText, "text/html")
        html.querySelector("overflow-list[defer]")?.removeAttribute("defer")

        const textContent = html.querySelector(`variant-custom-picker script[type="application/json"]`)?.textContent
        if (!textContent) return

        if (shouldMorphMain) {
          this.updateMain(html)
        } else {
          const newProduct = this.updateVariantPicker(html)
        }
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          console.log("Fetch aborted by user")
        } else {
          console.error(error)
        }
      })
      .finally(() => {
        // Only clear the loading state if *this* fetch is still the latest
        // in flight. When the user clicks rapidly, an earlier aborted fetch
        // reaches `.finally` while a newer one is still pending — clearing
        // then would hide the indicator prematurely.
        if (this.#abortController === controller) {
          this.#togglePriceUpdatingState(false)
        }
      })
  }

  /**
   * Picks the tightest ancestor that scopes this picker's price element(s).
   * Inside the upgrade-products-modal each <article class="upgrade-card">
   * renders its own bare price block, so the shopify-section is too wide
   * (it would match the main product's <product-price> too).
   */
  #priceScope() {
    return this.closest(".upgrade-card") || this.closest(".shopify-section") || document
  }

  /**
   * Adds/removes `.is-price-updating` on the price element(s) in scope
   * while a variant fetch is in flight. Covers both the main product page
   * (<product-price> wrapper) and the upgrade-modal cards (bare
   * <div ref="priceContainer">, no wrapper). The stylesheet in
   * blocks/custom-product-price.liquid handles both selectors.
   * @param {boolean} isUpdating
   */
  #togglePriceUpdatingState(isUpdating) {
    const scope = this.#priceScope()
    const targets = scope.querySelectorAll('product-price, [ref="priceContainer"]')
    targets.forEach((el) => el.classList.toggle("is-price-updating", isUpdating))
  }

  updateVariantPicker(newHtml) {
    let newProduct

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase())

    if (!newVariantPickerSource) {
      throw new Error("No new variant picker source found")
    }

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId
      const newProductUrl = newVariantPickerSource.dataset.productUrl

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl }
      }

      this.dataset.productId = newProductId
      this.dataset.productUrl = newProductUrl
    }

    const newProductJson = newVariantPickerSource.querySelector('script[type="application/json"]')?.textContent
    if (newProductJson) {
      try {
        this._product = JSON.parse(newProductJson)
      } catch (error) {
        console.warn("Product data unreadable", error)
      }
    }

    morph(this, newVariantPickerSource)
    syncDiscountBadge(newHtml)
    this.updateInventoryHint()
    this.highlightOptionsWithInventory()
    this.updateExternalPrice(newHtml)

    return newProduct
  }

  /**
   * Syncs the price element(s) in scope with the freshly rendered price
   * block from the fetched HTML. Handles three shapes:
   *   1. <product-price> wrapping a <price-aggregator> (Configurator/HighConfig
   *      products) — only `data-main-price` / `data-main-compare-at-price`
   *      get synced and `refresh()` is called, never the rendered children
   *      (would flash 0,00 € from the raw server base_price).
   *   2. <product-price> wrapping a <div ref="priceContainer"> (plain
   *      products) — morph children in place so data-price on the inner
   *      container updates without an element swap.
   *   3. Bare <div ref="priceContainer"> without any <product-price> wrapper
   *      (upgrade-products-modal cards) — same morph strategy as 2.
   * @param {Document} newHtml
   */
  updateExternalPrice(newHtml) {
    const scope = this.#priceScope()

    // --- Case 1/2: <product-price> wrapper ---
    const currentPriceEls = scope.querySelectorAll("product-price")
    if (currentPriceEls.length) {
      const newPriceEls = Array.from(newHtml.querySelectorAll("product-price"))
      if (!newPriceEls.length) return

      this.#syncProductPriceElements(currentPriceEls, newPriceEls)
      return
    }

    // --- Case 3: bare [ref="priceContainer"] (upgrade-modal) ---
    const currentContainer = scope.querySelector('[ref="priceContainer"]')
    if (!currentContainer) return

    // The fetched HTML is the upgrade product's own page: its price lives
    // inside a <product-price> (from blocks/custom-product-price.liquid).
    // Pull the inner [ref="priceContainer"] out of that wrapper.
    const newContainer =
      newHtml.querySelector('product-price [ref="priceContainer"]') ||
      newHtml.querySelector('[ref="priceContainer"]')
    if (!newContainer) return

    // Attribute sync (childrenOnly morph doesn't touch the host's own
    // attributes, but data-price on the container is read downstream).
    for (const { name, value } of Array.from(newContainer.attributes)) {
      currentContainer.setAttribute(name, value)
    }
    morph(currentContainer, newContainer, { childrenOnly: true })
  }

  /**
   * @param {NodeListOf<Element>} currentPriceEls
   * @param {Element[]} newPriceEls
   */
  #syncProductPriceElements(currentPriceEls, newPriceEls) {
    currentPriceEls.forEach((currentEl, index) => {
      const blockId = currentEl.dataset.blockId
      let newEl = null
      if (blockId) {
        newEl = newPriceEls.find((el) => el.dataset.blockId === blockId) || null
      }
      if (!newEl) newEl = newPriceEls[index] || newPriceEls[0]
      if (!newEl) return

      const currentAggregator = currentEl.querySelector("price-aggregator")
      const newAggregator = newEl.querySelector("price-aggregator")

      if (currentAggregator && newAggregator) {
        // Aggregator path: never morph the rendered children. The
        // server-rendered <span data-total-price> only contains the
        // bare base_price (often 0 for highly-configurable products),
        // so replacing it would briefly flash "0,00 €" before the
        // aggregator re-runs Main + Fabrics + Upgrades. Instead we
        // just sync the inputs the aggregator reads and let it
        // recompute the visible numbers itself.
        if (newAggregator.dataset.mainPrice) {
          currentAggregator.dataset.mainPrice = newAggregator.dataset.mainPrice
        }
        currentAggregator.dataset.mainCompareAtPrice = newAggregator.dataset.mainCompareAtPrice || ""
        currentAggregator.refresh?.()
        return
      }

      // Simple price block: morph children in place so the <product-price>
      // host survives and data attributes like data-price on the inner
      // container update without an element swap.
      morph(currentEl, newEl, { childrenOnly: true })
    })
  }

  updateMain(newHtml) {
    const main = document.querySelector("main")
    const newMain = newHtml.querySelector("main")

    if (!main || !newMain) {
      throw new Error("No new main source found")
    }

    morph(main, newMain)
  }

  hasInventoryManagement(variant) {
    if (!variant || !Object.prototype.hasOwnProperty.call(variant, "inventory_management")) {
      return false
    }
    const value = variant.inventory_management
    return typeof value === "string" && value.length > 0
  }

  getMinimumOrderQuantity(productOverride) {
    const product = productOverride ?? this._product
    const raw = product?.minimum_order_quantity
    const parsed = Number(raw)
    if (Number.isFinite(parsed) && parsed > 0) {
      return Math.max(1, Math.ceil(parsed))
    }
    return 1
  }

  isBundle() {
    // Check if the variant-custom-picker element has a data-bundle attribute
    return this.hasAttribute("data-bundle") || this.hasAttribute("data-is-bundle")
  }

  isProductPage() {
    if (this.hasAttribute("data-template-product-match")) {
      return true
    }

    return false
  }

  findSelectedVariant() {
    const product = this._product
    if (!product) {
      console.warn("[DEBUG] No product found")
      return null
    }

    const variants = product.variants ?? []
    const allOptions = product.options ?? []
    const visibleOptions = allOptions.filter((opt) => !this.isHiddenOption(opt))

    const selected = {}
    visibleOptions.forEach((optionName) => {
      const sel = this.querySelector(`select[name="options[${optionName}]"]`)
      if (sel instanceof HTMLSelectElement) {
        selected[optionName] = sel.value
        // console.log("[DEBUG] Selected (select):", optionName, "=", sel.value)
        return
      }
      const fs = this.querySelector(`.variant-option [data-option-name="${optionName}"]`)?.closest(".variant-option")
      const checked = fs?.querySelector('input[type="radio"]:checked')
      if (checked) {
        selected[optionName] = checked.value
        // console.log("[DEBUG] Selected (radio):", optionName, "=", checked.value)
      }
    })

    // Multi-merchant smart routing: when several Merchant siblings share the
    // visible-option combo, pick the one with the highest tracked stock that
    // covers the requested quantity. Prices/featured_media must be identical
    // across merchant siblings — otherwise PDP price would not match cart.
    const qtyInputEl = document.querySelector('input[name="quantity"]')
    const requestedQty = qtyInputEl
      ? Math.max(1, parseInt(/** @type {HTMLInputElement} */ (qtyInputEl).value, 10) || 1)
      : 1
    const resolvedId = resolveBestMerchantVariant(product, selected, requestedQty)
    if (resolvedId != null) {
      const resolved = variants.find((v) => String(v.id) === String(resolvedId))
      if (resolved) {
        this._lastResolverMeta = { resolvedId, requestedQty, selected, source: "resolver" }
        return resolved
      }
    }

    const matchingVariant = variants.find((variant) => {
      if (!variant.available) return false

      const matches = visibleOptions.every((opt) => {
        const selVal = selected[opt]
        if (!selVal) return true
        const idx = allOptions.indexOf(opt)
        const variantVal = variant[`option${idx + 1}`]
        const match = variantVal === selVal

        return match
      })

      return matches
    })

    if (matchingVariant) {
      this._lastResolverMeta = { resolvedId: null, requestedQty, selected, source: "fallback-first-available" }
    }
    return matchingVariant || null
  }

  syncVariantFromSelection() {
    const selectedVariant = this.findSelectedVariant()

    if (!selectedVariant) {
      // console.warn("[DEBUG] No matching variant found")
      return
    }

    const variantInput = this.$scope?.querySelector("input[data-selected-variant-id]")

    if (variantInput) {
      const oldId = variantInput.getAttribute("value")
      const newId = String(selectedVariant.id)

      variantInput.setAttribute("value", newId)

      const isOnProductPage = this.isProductPage()

      // Update Url with variant ID
      if (isOnProductPage) {
        const url = new URL(window.location.href)
        url.searchParams.set("variant", newId)
        history.replaceState({}, "", url.toString())
      }

      this.dispatchEvent(
        new CustomEvent("variant:selected", {
          detail: {
            variantId: selectedVariant.id,
            variant: selectedVariant,
            resource: selectedVariant,
            source: "selection-sync",
          },
          bubbles: true,
        }),
      )

      // force update prices only for Bundles (plain products update their price
      // via the morph in updateVariantPicker); the media gallery, however, is not
      // touched by that morph and the picker never dispatches a VariantUpdateEvent,
      // so reload it here — otherwise variant images never swap on plain products.
      const isBundle = this.isBundle()
      if (isBundle) {
        this.forceUpdatePrice(selectedVariant)
      }

      this.forceUpdateImage(selectedVariant)
    }
  }

  forceUpdatePrice(variant) {
    if (!variant) return

    const productData = this._product
    if (!productData) {
      console.warn("[PRICE] No product data available")
      return
    }

    const variantData = productData.variants?.find((v) => v.id === variant.id)
    if (!variantData) {
      console.warn("[PRICE] Variant data not found for ID:", variant.id)
      return
    }

    const priceContainer = this.$scope?.querySelector(".product__price") || document.querySelector(".product__price")

    if (!priceContainer) {
      console.warn("[PRICE] No .product__price container found")
      return
    }

    const priceSpan = priceContainer.querySelector(".price")

    if (!priceSpan) {
      console.warn("[PRICE] No .price element found")
      return
    }

    // Set currency and currency format

    let currency = "€"

    if (window.Shopify?.currency?.active) {
      currency = window.Shopify.currency.active
    } else if (window.Theme?.currency) {
      currency = window.Theme.currency
    }

    const formatPrice = (cents) => {
      if (!cents && cents !== 0) return null
      const value = cents / 100

      const formatted = value.toFixed(2)
      const [whole, decimal] = formatted.split(".")

      if (currency === "CHF") {
        const withThousands = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        return `${currency} ${withThousands}.${decimal} `
      } else {
        const withThousands = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
        return `${withThousands},${decimal} ${currency}`
      }
    }

    // Update prices

    const newPrice = formatPrice(variantData.price)
    const newComparePrice = formatPrice(variantData.compare_at_price)
    const priceTextSpan = priceSpan.querySelector("span:first-child")

    if (priceTextSpan && newPrice) {
      const taxText = priceTextSpan.querySelector("span")?.outerHTML || ""
      priceTextSpan.innerHTML = `${newPrice}${taxText}`
    }

    // update compare at price element --> update
    const compareAtPriceEl = priceSpan.querySelector("[data-compare-at]")

    if (compareAtPriceEl) {
      if (newComparePrice) {
        const listText = compareAtPriceEl.textContent.split(/\d/)[0]
        compareAtPriceEl.textContent = `${listText}${newComparePrice}*`
      }
    } else {
      // console.log("[PRICE] No .compare-at-price element in DOM - skipped")
    }

    if (newPrice) {
      priceContainer.setAttribute("data-price", newPrice)
    }
  }

  forceUpdateImage(variant) {
    if (!variant) return

    // The main PDP picker owns a full, server-rendered <media-gallery> (thumbnails,
    // slideshow, zoom), so re-fetch the section and morph it in. Scoped pickers
    // (upgrade-products modal, quick-add) instead sit beside a lightweight
    // single-image gallery inside their own card — there we swap just the <img>.
    // reloadMediaGallery must never run there: it targets the document-level
    // [data-media-gallery] + window.location and would clobber the main PDP gallery
    // with the wrong product's variant.
    if (this.isProductPage()) {
      this.reloadMediaGallery(variant.id)
    } else {
      this.updateScopedImage(variant)
    }
  }

  /**
   * Swaps the single image in this picker's own card (upgrade-products modal /
   * quick-add) to the selected variant's featured media. Scoped to the closest
   * .upgrade-card so sibling cards' galleries are never touched, and reads the
   * image straight from the variant JSON — no section fetch (the card's
   * data-section-id is the product id, not a renderable section handle).
   * @param {object} variant - The selected variant from this._product.variants.
   */
  updateScopedImage(variant) {
    const scope = this.closest(".upgrade-card")
    if (!scope) return

    const img = scope.querySelector("[data-media-gallery] img")
    const media = variant?.featured_media
    const src = media?.preview_image?.src
    if (!img || !src) return

    // Match the width the card originally rendered at (image_url: width: 600) so we
    // don't pull the full-size original into a 120px thumbnail.
    let nextSrc = src
    try {
      const url = new URL(src, window.location.origin)
      url.searchParams.set("width", "600")
      nextSrc = url.toString()
    } catch (e) {
      /* keep original src on malformed URL */
    }

    img.src = nextSrc
    // The rendered <img> carries a width-scoped srcset from image_tag; drop it so the
    // browser doesn't keep serving the previous variant's responsive candidates.
    img.removeAttribute("srcset")
    if (media.alt != null) img.alt = media.alt
  }

  async reloadMediaGallery(variantId) {
    const mediaGallery = document.querySelector("[data-media-gallery]")
    if (!mediaGallery) {
      console.warn("[IMAGE] No media-gallery found")
      return
    }

    // Bundle products flagged custom.show_in_preview show their selected variant as an overlay thumbnail
    // (bundle-component-preview.js); the gallery itself must stay untouched, like with
    // product_add.fabric_color.
    if (mediaGallery.querySelector("[data-bundle-preview]")) return

    const sectionId = mediaGallery.dataset.sectionId
    if (!sectionId) {
      console.warn("[IMAGE] No section-id found")
      return
    }

    // Build URL for the section with new variant
    const url = new URL(window.location.href)
    url.searchParams.set("variant", variantId)
    url.searchParams.set("section_id", sectionId)

    // console.log("[IMAGE] Loading section from:", url.toString())

    try {
      const response = await fetch(url.toString())
      const html = await response.text()

      const parser = new DOMParser()
      const doc = parser.parseFromString(html, "text/html")

      // Find new Media Gallery
      const newMediaGallery = doc.querySelector("[data-media-gallery]")

      if (!newMediaGallery) {
        console.warn("[IMAGE] No new media-gallery found in response")
        return
      }
      morph(mediaGallery, newMediaGallery)
    } catch (error) {
      console.error("[IMAGE] Error loading gallery:", error)
    }
  }

  initializeWhenReady() {
    if (this.isFullyRendered()) {
      this.performInitialization()
      return
    }

    // wait for requestAnimationFrame
    requestAnimationFrame(() => {
      if (this.isFullyRendered()) {
        this.performInitialization()
        return
      }

      setTimeout(() => {
        if (this.isFullyRendered()) {
          this.performInitialization()
          return
        }

        // wait for 200ms as fallback
        setTimeout(() => {
          this.performInitialization()
        }, 200)
      }, 50)
    })
  }

  isFullyRendered() {
    const hasOptions = this.querySelectorAll('select[name^="options"], input[type="radio"]').length > 0
    const hasProductData = !!this._product
    const hasScope = !!this.$scope

    return hasOptions && hasProductData && hasScope
  }

  performInitialization() {
    this.syncVariantFromSelection()
    this.updateInventoryHint()
    this.highlightOptionsWithInventory()

    // Force-Updates only for Bundles
    const isBundle = this.isBundle()

    if (isBundle) {
      setTimeout(() => {
        const selectedVariant = this.findSelectedVariant()
        if (selectedVariant) {
          this.forceUpdatePrice(selectedVariant)
          this.forceUpdateImage(selectedVariant)
        }
      }, 100)
    } else {
      // console.log("[DEBUG] Not a bundle, skipping force updates")
    }
  }

  // GETTER Helper
  get selectedOption() {
    const selectedOption = this.querySelector("select option[selected], fieldset input:checked")

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined
    }

    return selectedOption
  }

  get selectedOptionId() {
    const { selectedOption } = this
    if (!selectedOption) return undefined
    const { optionValueId } = selectedOption.dataset

    if (!optionValueId) {
      throw new Error("No option value ID found") // without "#"
    }

    return optionValueId
  }

  get selectedOptionsValues() {
    const selectedOptions = Array.from(this.querySelectorAll("select option[selected], fieldset input:checked"))

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset

      if (!optionValueId) throw new Error("No option value ID found")

      return optionValueId
    })
  }
}

if (!customElements.get("variant-custom-picker")) {
  customElements.define("variant-custom-picker", VariantCustomPicker)
}
